import {
  PropsWithChildren,
  createContext,
  useContext,
  useRef,
  useState,
} from "react";
import { useMutation } from "react-query";
import { saveChanges } from "../api/saveChanges";
import { SandpackBundlerFiles } from "@codesandbox/sandpack-client";
import { Environment } from "../config/endpoints";
import { useTracking } from "../tracking";
import { PlaygroundAuthContext } from "./PlaygroundAuth";

interface SaveArgs {
  sessionId: string;
  files: SandpackBundlerFiles;
  environment: Environment;
}

interface AuthenticatedSaveArgs extends SaveArgs {
  token: string;
}

export const SaveChangesContext = createContext<{
  save: (args: SaveArgs) => Promise<void>;
  isSaving: boolean;
  touchedFiles: Set<string>;
  isDirty: boolean;
  saveCount: number;
  canSave: boolean;
  onFileChange: (fileName: string | string[]) => void;
}>({
  save: () => Promise.resolve(),
  isSaving: false,
  touchedFiles: new Set(),
  isDirty: false,
  saveCount: 0,
  canSave: false,
  onFileChange: () => null,
});

export function SaveChangesProvider(props: PropsWithChildren) {
  const [touchedFiles, setTouchedFiles] = useState<Set<string>>(
    () => new Set(),
  );
  const prevFilesRef = useRef<Set<string>>(new Set());
  const { trackEvent } = useTracking();
  const { token, canSave } = useContext(PlaygroundAuthContext);
  const [saveCount, setSaveCount] = useState(0);
  const isDirty = touchedFiles.size > 0;

  const { status, mutateAsync } = useMutation<
    void,
    unknown,
    AuthenticatedSaveArgs
  >({
    mutationFn: (args) => saveChanges(args),
    retry: false,
    onSuccess() {
      trackEvent("playground.save.success");
      setSaveCount((count) => count + 1);
    },
    onMutate() {
      trackEvent("playground.save.start");
      prevFilesRef.current = new Set(touchedFiles);
      setTouchedFiles(new Set());
    },
    onError() {
      trackEvent("playground.save.error");
      setTouchedFiles(
        (current) => new Set([...prevFilesRef.current, ...current]),
      );
    },
    onSettled() {
      prevFilesRef.current = new Set();
    },
  });

  const save = (args: SaveArgs) => {
    if (!token)
      return Promise.reject(new Error("Authentication is required to save"));
    return mutateAsync({ ...args, token });
  };

  const onFileChange = (fileName: string | string[]) => {
    if (Array.isArray(fileName)) {
      setTouchedFiles((prev) => new Set([...prev, ...fileName]));
      return;
    }
    setTouchedFiles((prev) => new Set([...prev, fileName]));
  };

  const isSaving = status === "loading";

  return (
    <SaveChangesContext.Provider
      value={{
        save,
        onFileChange,
        isSaving,
        touchedFiles,
        isDirty,
        saveCount,
        canSave,
      }}
    >
      {props.children}
    </SaveChangesContext.Provider>
  );
}
